import { useEffect, useRef, useState } from 'react';
import type { SessionState } from '@/api/charging';

export interface EtaPoint {
  /** Minutos restantes (arredondado, >= 1). */
  min: number;
  /** Horário previsto local, "HH:MM". */
  at: string;
}

export interface ChargeEta {
  /** null quando o SoC já passou de 80%. */
  to80: EtaPoint | null;
  to100: EtaPoint;
}

// Janela deslizante das amostras de SoC. Curta demais = estimativa nervosa;
// longa demais = demora a refletir queda de potência perto do fim da carga.
const WINDOW_MS = 5 * 60_000;
// Estimativa só aparece depois de estabilizar: >= 3 amostras cobrindo >= 90s.
// Antes disso os cards ficam escondidos (padrão "só dado real" do app).
const MIN_SAMPLES = 3;
const MIN_SPAN_MS = 90_000;

interface Sample {
  t: number;
  soc: number;
}

function fmtAt(msFromNow: number): string {
  const d = new Date(Date.now() + msFromNow);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function etaPoint(msFromNow: number): EtaPoint {
  return { min: Math.max(1, Math.round(msFromNow / 60_000)), at: fmtAt(msFromNow) };
}

/**
 * Estimativa client-side de "até 80%" / "até 100%" a partir da taxa de
 * variação do SoC — o backend não fornece ETA (decisão Saulo 2026-07-08,
 * tela Imersiva do mapfirst-charging.jsx). Retorna null até a estimativa
 * estabilizar, quando a taxa é <= 0, ou em carregador AC (nunca manda SoC)
 * — quem consome esconde os cards nesses casos.
 *
 * Regressão linear simples (mínimos quadrados) sobre a janela: mais estável
 * que usar só o primeiro/último ponto quando o SoC chega em passos de 1%.
 */
export function useChargeEta(
  socPercent: number | null,
  state: SessionState
): ChargeEta | null {
  const samplesRef = useRef<Sample[]>([]);
  const [eta, setEta] = useState<ChargeEta | null>(null);

  useEffect(() => {
    if (state !== 'active' || socPercent === null) {
      if (state !== 'active') {
        samplesRef.current = [];
        setEta(null);
      }
      return;
    }

    const now = Date.now();
    const samples = samplesRef.current;
    const last = samples[samples.length - 1];
    // SoC regrediu (releitura/reset do carregador)? Janela anterior não vale.
    if (last && socPercent < last.soc) samples.length = 0;
    if (!last || last.soc !== socPercent) {
      samples.push({ t: now, soc: socPercent });
    }
    while (samples.length > 0 && now - samples[0].t > WINDOW_MS) {
      samples.shift();
    }

    if (samples.length < MIN_SAMPLES || now - samples[0].t < MIN_SPAN_MS) {
      setEta(null);
      return;
    }

    // Mínimos quadrados: soc = a + b*t  (b em %/ms)
    const n = samples.length;
    const tBase = samples[0].t;
    let sumT = 0;
    let sumS = 0;
    let sumTT = 0;
    let sumTS = 0;
    for (const s of samples) {
      const t = s.t - tBase;
      sumT += t;
      sumS += s.soc;
      sumTT += t * t;
      sumTS += t * s.soc;
    }
    const denom = n * sumTT - sumT * sumT;
    if (denom === 0) {
      setEta(null);
      return;
    }
    const rate = (n * sumTS - sumT * sumS) / denom; // %/ms
    if (rate <= 0) {
      setEta(null);
      return;
    }

    const msTo = (target: number) => ((target - socPercent) / rate);
    setEta({
      to80: socPercent < 80 ? etaPoint(msTo(80)) : null,
      to100: etaPoint(msTo(100))
    });
  }, [socPercent, state]);

  return eta;
}
